import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { Plus } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Banner } from '@/components/Banner';
import { Button } from '@/components/Button';
import { Card, Seccion } from '@/components/Card';
import { Casilla } from '@/components/Casilla';
import { NumberField } from '@/components/Field';
import { Segmentado } from '@/components/Segmentado';
import { actualizarSettings, leerSettings } from '@/db/schema';
import { mensajeDeError } from '@/db/tx';
import {
  barraYDiscosPredeterminados,
  discosLimpios,
  discosPorLado,
  MOTIVO_SIN_DISCOS,
  textoPorLado,
  validarPesoDeDisco,
  type BarraYDiscos,
} from '@/domain/discos';
import { formatearNumero, leerDecimal } from '@/domain/numbers';
import { aKg, desdeKg, esUnidadPeso, UNIDADES_PESO, type UnidadPeso } from '@/domain/units';

const numero = (v: number) => formatearNumero(v, { decimales: 2, fijos: false });

/**
 * Calculadora de discos (Req. 26.3, 26.4): con el peso que quieres levantar,
 * dice qué discos poner de cada lado y, si no da exacto, cuánto falta.
 *
 * La barra y los discos se configuran aquí mismo, en kg o en lb, y se guardan
 * en esa unidad: el número grabado en el disco es el dato real. Se llega con
 * `?objetivo=100&unidad=lb` desde el entrenamiento, y si esa unidad no es la
 * de tus discos, el peso se convierte al entrar.
 */
export function DiscosPage() {
  const ajustes = useLiveQuery(() => leerSettings(), []);
  const [parametros] = useSearchParams();
  const [escrito, setEscrito] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  if (ajustes === undefined) return null;

  const config = ajustes.barraYDiscos ?? barraYDiscosPredeterminados(ajustes.unidadPeso);
  const guardar = async (cambios: BarraYDiscos) => {
    setError(null);
    try {
      await actualizarSettings({ barraYDiscos: cambios });
    } catch (e) {
      setError(mensajeDeError(e));
    }
  };

  // El objetivo que llega del entrenamiento, en la unidad de los discos
  const delParametro = (() => {
    const valor = leerDecimal(parametros.get('objetivo') ?? '');
    if (valor === null || valor <= 0) return '';
    const suya = parametros.get('unidad');
    if (!esUnidadPeso(suya) || suya === config.unidad) return String(valor);
    return String(Number(numero(desdeKg(aKg(valor, suya), config.unidad)).replace(/,/g, '')));
  })();
  const texto = escrito ?? delParametro;
  const objetivo = leerDecimal(texto);
  const resultado = objetivo === null ? null : discosPorLado(objetivo, config);

  return (
    <div className="space-y-6">
      <PageHeader
        titulo="¿Qué discos pongo?"
        descripcion="Escribe el peso que quieres levantar y te digo los discos de cada lado."
        volver={{ a: '/gym', texto: 'Gym', etiqueta: 'Volver a Gym' }}
      />

      <NumberField
        etiqueta={`Peso que quieres levantar (${config.unidad})`}
        valor={texto}
        onCambio={setEscrito}
        autoFocus
        ayuda={`Con la barra incluida. Tu barra pesa ${numero(config.barra)} ${config.unidad}.`}
      />

      {resultado !== null && (
        <Card>
          {resultado.tipo === 'no_se_puede' ? (
            <>
              <span className="antetitulo">No se puede</span>
              <p className="mt-2 text-[15px] leading-relaxed text-texto">{MOTIVO_SIN_DISCOS[resultado.motivo]}</p>
            </>
          ) : (
            <>
              <span className="antetitulo">Por lado</span>
              <p className="mt-2 text-[28px] leading-tight tracking-[-0.8px] tabular-nums text-texto">
                {textoPorLado(resultado.porLado, config.unidad)}
              </p>
              <p className="mt-2 text-[13px] leading-relaxed text-texto-suave">
                {resultado.diferencia === 0
                  ? `Son ${numero(resultado.total)} ${config.unidad} en total, justo lo que querías.`
                  : `Son ${numero(resultado.total)} ${config.unidad} en total: ${
                      resultado.diferencia > 0 ? 'faltan' : 'sobran'
                    } ${numero(Math.abs(resultado.diferencia))} ${config.unidad} para ${numero(objetivo ?? 0)}. Con tus discos no da exacto.`}
              </p>
              {resultado.porLado.length > 0 && (
                <ul className="mt-3 divide-y divide-borde border-t border-borde">
                  {resultado.porLado.map((d) => (
                    <li key={d.disco} className="flex justify-between gap-3 py-2 text-[14px] text-texto">
                      <span>
                        {d.cuantos === 1 ? 'Un disco' : `${d.cuantos} discos`} de {numero(d.disco)} {config.unidad}
                      </span>
                      <span className="tabular-nums text-texto-suave">{numero(d.disco * d.cuantos)} {config.unidad}</span>
                    </li>
                  ))}
                </ul>
              )}
              <details className="mt-3 border-t border-borde pt-2">
                <summary className="flex min-h-11 cursor-pointer items-center text-[13px] font-medium text-marca">
                  De dónde sale
                </summary>
                <ol className="space-y-1.5 pb-1 text-[12px] leading-relaxed text-texto-suave">
                  {resultado.pasos.map((p) => (
                    <li key={p}>{p}</li>
                  ))}
                </ol>
              </details>
            </>
          )}
        </Card>
      )}

      {error !== null && (
        <Banner tono="alerta" titulo="No se guardó">
          {error}
        </Banner>
      )}

      <BarraYDiscosForm
        config={config}
        onGuardar={(c) => void guardar(c)}
        onCambioDeUnidad={(u) => {
          // El peso escrito era en la otra unidad: se limpia para no leerlo mal
          setEscrito('');
          void guardar(barraYDiscosPredeterminados(u));
        }}
      />
    </div>
  );
}

/** La barra y los discos del gimnasio, en kg o en lb (Req. 26.4). */
function BarraYDiscosForm({
  config,
  onGuardar,
  onCambioDeUnidad,
}: {
  config: BarraYDiscos;
  onGuardar: (c: BarraYDiscos) => void;
  onCambioDeUnidad: (u: UnidadPeso) => void;
}) {
  const [barra, setBarra] = useState<string | null>(null);
  const [nuevo, setNuevo] = useState('');
  const [aviso, setAviso] = useState<string | null>(null);
  const textoBarra = barra ?? numero(config.barra);
  const predeterminados = barraYDiscosPredeterminados(config.unidad).discos;
  // Los típicos de la unidad más los que agregó, de mayor a menor
  const todos = discosLimpios([...predeterminados, ...config.discos]);
  const hay = new Set(config.discos);

  function alternar(disco: number, marcado: boolean) {
    const discos = marcado ? [...config.discos, disco] : config.discos.filter((d) => d !== disco);
    if (discos.length === 0) return setAviso('Deja al menos un disco: si no, no hay nada que calcular.');
    setAviso(null);
    onGuardar({ ...config, discos: discosLimpios(discos) });
  }

  function cambiarBarra(t: string) {
    setBarra(t);
    const valor = leerDecimal(t);
    if (valor === null || valor < 0) return setAviso('Escribe lo que pesa tu barra, o 0 si no usas barra.');
    setAviso(null);
    onGuardar({ ...config, barra: valor });
  }

  function agregar(evento: FormEvent) {
    evento.preventDefault();
    const valor = leerDecimal(nuevo);
    const malo = validarPesoDeDisco(valor);
    if (malo !== null) return setAviso(malo);
    if (valor !== null && hay.has(valor)) return setAviso('Ese disco ya está en tu lista.');
    setAviso(null);
    setNuevo('');
    onGuardar({ ...config, discos: discosLimpios([...config.discos, valor as number]) });
  }

  return (
    <Seccion
      titulo="Tu barra y tus discos"
      descripcion="Marca los discos que de verdad hay en tu gimnasio. Se guardan tal como están grabados: un disco de 45 lb no son 20.41 kg."
    >
      <div className="space-y-4">
        <Segmentado
          etiqueta="Unidad de la barra y los discos"
          etiquetaVisible
          completo
          valor={config.unidad}
          onCambio={(u: UnidadPeso) => {
            setBarra(null);
            setAviso(null);
            // Los discos de un gimnasio en lb no son los mismos números que en kg
            onCambioDeUnidad(u);
          }}
          opciones={UNIDADES_PESO.map((u) => ({ valor: u, etiqueta: u }))}
        />
        <p className="text-[12px] leading-relaxed text-texto-suave">
          Al cambiar la unidad quedan la barra y los discos típicos de esa unidad; ajústalos abajo si los tuyos son
          otros.
        </p>

        <NumberField
          etiqueta={`Peso de la barra (${config.unidad})`}
          valor={textoBarra}
          onCambio={cambiarBarra}
          ayuda="0 si el ejercicio no lleva barra."
        />

        <fieldset className="grid gap-2">
          <legend className="mb-1 text-[13px] text-texto-suave">Discos que hay, de cada lado</legend>
          <div className="grid grid-cols-2 gap-2">
            {todos.map((d) => (
              <Casilla key={d} marcada={hay.has(d)} onCambio={(m) => alternar(d, m)}>
                {numero(d)} {config.unidad}
              </Casilla>
            ))}
          </div>
        </fieldset>

        <form onSubmit={agregar} className="grid gap-2" noValidate>
          <NumberField etiqueta={`Agregar otro disco (${config.unidad})`} valor={nuevo} onCambio={setNuevo} />
          <Button type="submit" variante="secundario" icono={Plus}>
            Agregar este disco
          </Button>
        </form>

        {aviso !== null && (
          <Banner tono="aviso" titulo={aviso}>
            Lo demás se queda como estaba.
          </Banner>
        )}
      </div>
    </Seccion>
  );
}
